import { Search as SearchIcon } from '@mui/icons-material';
import { Popper } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { hasFieldHelpIcon, normalizeFieldHelpProps, VNFieldHelpLayout } from '@/components/VNFieldHelp';

import VNDropdownDisplay from './input/VNDropdownDisplay';
import VNDropdownList from './options/VNDropdownList';
import type { DisplaySlotProps, DropdownOption, FilterOption, ListSlotProps } from './types';
import { filterOptionsWithAncestors } from './types';

export interface VNDropdownBaseProps extends VNFieldHelpProps {
  name?: string;
  options: DropdownOption[];
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  error?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  listComponent?: React.ComponentType<ListSlotProps>;
  inputRef?: React.Ref<HTMLButtonElement>;
  disableBorder?: boolean;
  compact?: boolean;
  popoverWidth?: number;
  popoverMinHeight?: number;
  className?: string;
  insetSearch?: boolean;
  /** Notifies when the inset search query changes (including clear to `''` on close). */
  onInsetQueryChange?: (query: string) => void;
  /** Propagated from VNDropdown when the original listComponent has a minSelections static marker. */
  minSelections?: number;
  filterOptions?: FilterOption[];
  isLiveLoading?: boolean;
  /**
   * Fallback options used only for display of pre-selected values that may not yet appear in the
   * main `options` array (e.g. live-search dropdowns where the initial fetch hasn't loaded the
   * selected item). These options are merged into `selectedOptions` but never shown in the list.
   */
  seedOptions?: DropdownOption[];
  allowClear?: boolean;
}

const VNDropdownBase = ({
  name,
  options,
  value,
  onChange,
  onBlur,
  error,
  label,
  placeholder,
  disabled = false,
  multiple = false,
  displayComponent: DisplayComponent = VNDropdownDisplay,
  listComponent: ListComponent = VNDropdownList,
  inputRef,
  disableBorder = false,
  compact = false,
  popoverWidth,
  popoverMinHeight,
  className,
  insetSearch = false,
  onInsetQueryChange,
  minSelections: minSelectionsProp,
  filterOptions,
  isLiveLoading = false,
  seedOptions,
  allowClear = false,
  ...fieldHelpProps
}: VNDropdownBaseProps) => {
  const normalized = normalizeFieldHelpProps(fieldHelpProps);
  const { dropDownItemHelperTooltip, dropDownItemHelperText } = normalized;
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const minSelections: number = minSelectionsProp ?? (ListComponent as any).minSelections ?? 0;
  const uid = useId();
  const listboxId = `vn-dropdown-listbox${uid}`;
  const triggerId = `vn-dropdown-trigger${uid}`;
  const optionIdPrefix = `vn-dropdown-option${uid}`;

  const anchorRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLElement>(null);
  const insetSearchRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');
  const [insetQuery, setInsetQuery] = useState('');
  const [popoverMinWidth, setPopoverMinWidth] = useState(336);
  const formMethods = useFormContext();

  // Latest-value ref — keeps onChange out of the auto-select effect's deps.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onInsetQueryChangeRef = useRef(onInsetQueryChange);
  onInsetQueryChangeRef.current = onInsetQueryChange;

  const setInsetQueryAndNotify = useCallback((next: string) => {
    setInsetQuery(next);
    onInsetQueryChangeRef.current?.(next);
  }, []);

  const displayedOptions = useMemo(
    () => (insetSearch ? filterOptionsWithAncestors(options, insetQuery) : options),
    [insetSearch, insetQuery, options]
  );

  useEffect(() => {
    if (insetSearch) setFocusedIndex(0);
  }, [insetSearch, insetQuery]);

  useEffect(() => {
    if (insetSearch && isOpen) {
      const id = setTimeout(() => insetSearchRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [insetSearch, isOpen]);

  const selectedIds: Array<DropdownOption['id']> = useMemo(() => {
    if (value === null || value === undefined) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // 200ms delay after clear so re-selection doesn't feel like it ignored the interaction.
  const restoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsLoadedRef = useRef(false);
  useEffect(() => {
    if (minSelections > 0 && multiple && options.length > 0) {
      if (selectedIds.length === 0) {
        const delay = optionsLoadedRef.current ? 200 : 0;
        restoreTimerRef.current = setTimeout(() => {
          const restored = options.slice(0, minSelections).map((o) => o.id);
          onChangeRef.current?.(restored);
          if (formMethods?.resetField) {
            formMethods.resetField(name, {
              defaultValue: restored as any,
              keepDirty: false,
            });
          }
        }, delay);
      } else if (restoreTimerRef.current !== null) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      optionsLoadedRef.current = true;
    }
    return () => {
      if (restoreTimerRef.current !== null) clearTimeout(restoreTimerRef.current);
    };
  }, [options, selectedIds, minSelections, multiple]);

  const selectedOptions = useMemo(() => {
    const fromOptions = options.filter((o) => selectedIds.includes(o.id));
    if (!seedOptions?.length) return fromOptions;
    const foundIds = new Set(fromOptions.map((o) => o.id));
    const fromSeed = seedOptions.filter((o) => selectedIds.includes(o.id) && !foundIds.has(o.id));
    return [...fromOptions, ...fromSeed];
  }, [options, seedOptions, selectedIds]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      setPopoverAlign(spaceRight < 320 ? 'right' : 'left');
      setPopoverMinWidth(compact ? 320 : Math.max(anchorRef.current.offsetWidth, 336));
    }
    const firstSelectedIndex = displayedOptions.findIndex((o) => selectedIds.includes(o.id));
    setFocusedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : 0);
  }, [disabled, displayedOptions, selectedIds, compact]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    setInsetQueryAndNotify('');
    onBlur?.();
  }, [onBlur, setInsetQueryAndNotify]);

  // The MUI invisible backdrop intercepts all clicks outside the Paper (including
  // the anchor element) because it sits at z-index 1300 in the modal portal.
  // We disable its pointer-events so anchor elements receive clicks normally, and
  // replace outside-click detection with a document-level mousedown listener.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const insideAnchor = anchorRef.current?.contains(e.target as Node) ?? false;
      const insidePaper = paperRef.current?.contains(e.target as Node) ?? false;
      if (!insideAnchor && !insidePaper) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [isOpen, handleClose]);

  const handleToggle = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleClose, handleOpen]);

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      if (option.disabled) return;
      if (multiple) {
        const isSelected = selectedIds.includes(option.id);
        const next = isSelected ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id];
        onChange(next);
      } else {
        onChange(option.id);
        handleClose();
      }
    },
    [multiple, selectedIds, onChange, handleClose]
  );

  const handleClearOption = useCallback(
    (id: DropdownOption['id']) => {
      if (multiple) {
        onChange(selectedIds.filter((sid) => sid !== id));
      } else {
        onChange(null);
      }
    },
    [multiple, selectedIds, onChange]
  );

  const handleClearAll = useCallback(() => {
    onChange(multiple ? [] : null);
  }, [multiple, onChange]);

  const handleSelectAll = useCallback(
    (opts: DropdownOption[]) => {
      const allIds = opts.filter((o) => !o.disabled).map((o) => o.id);
      if (allIds.length === 0) return;
      const allSelected = allIds.every((id) => selectedIds.includes(id));
      if (allSelected) {
        onChange(selectedIds.filter((id) => !allIds.includes(id)));
      } else {
        onChange(Array.from(new Set([...selectedIds, ...allIds])));
      }
    },
    [selectedIds, onChange]
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) {
            handleOpen();
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          handleClose();
          break;
        default:
          break;
      }
    },
    [isOpen, displayedOptions, focusedIndex, handleOpen, handleClose, handleSelect]
  );

  const handleInsetKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
            if (!multiple) return; // handleSelect calls handleClose for single-select
            setInsetQueryAndNotify('');
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          handleClose();
          break;
        default:
          break;
      }
    },
    [displayedOptions, focusedIndex, handleSelect, handleClose, multiple, setInsetQueryAndNotify]
  );

  const focusedOptionId = isOpen && focusedIndex >= 0 ? `${optionIdPrefix}-${focusedIndex}` : undefined;

  const displayProps = useMemo<DisplaySlotProps>(
    () => ({
      selectedOptions,
      isOpen,
      onToggle: handleToggle,
      onClearOption: handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      inputRef,
      triggerId,
      listboxId,
      focusedOptionId,
      onKeyDown: handleKeyDown,
      disableBorder,
      compact,
      allowClear,
      onClearAll: handleClearAll,
      hideFloatingLabel: false,
      ...fieldHelpProps,
    }),
    [
      selectedOptions,
      isOpen,
      handleToggle,
      handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      inputRef,
      triggerId,
      listboxId,
      focusedOptionId,
      handleKeyDown,
      disableBorder,
      compact,
      allowClear,
      handleClearAll,
      fieldHelpProps,
    ]
  );

  const listProps = useMemo<ListSlotProps>(
    () => ({
      options: displayedOptions,
      selectedIds,
      onSelect: handleSelect,
      onClose: handleClose,
      onSelectAll: handleSelectAll,
      multiple,
      focusedIndex,
      listboxId,
      optionIdPrefix,
      insetQuery,
      filterOptions,
      isLiveLoading,
      dropDownItemHelperTooltip,
      dropDownItemHelperText,
    }),
    [
      displayedOptions,
      selectedIds,
      handleSelect,
      handleClose,
      handleSelectAll,
      multiple,
      focusedIndex,
      listboxId,
      optionIdPrefix,
      insetQuery,
      filterOptions,
      isLiveLoading,
      dropDownItemHelperTooltip,
      dropDownItemHelperText,
    ]
  );

  const popoverPaperSx = useMemo(
    () => ({
      width: popoverWidth ?? popoverMinWidth,
      ...(popoverMinHeight !== undefined && { minHeight: popoverMinHeight }),
    }),
    [popoverWidth, popoverMinWidth, popoverMinHeight]
  );

  const dropdownContent = (
    <div
      className={['vn-dropdown', compact ? 'vn-dropdown--compact' : '', className ?? ''].filter(Boolean).join(' ')}
      ref={anchorRef}
    >
      <DisplayComponent {...displayProps} />
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement={popoverAlign === 'right' ? 'bottom-end' : 'bottom-start'}
        disablePortal
        style={{ zIndex: 1300 }}
      >
        <div
          ref={paperRef as React.RefObject<HTMLDivElement>}
          className="vn-dropdown__paper"
          style={{
            ...popoverPaperSx,
            pointerEvents: 'auto',
          }}
        >
          {insetSearch && (
            <div className="vn-dropdown__inset-search">
              <div className="vn-dropdown__inset-search-field">
                <SearchIcon className="vn-dropdown__inset-search-icon" fontSize="small" aria-hidden="true" />
                <input
                  ref={insetSearchRef}
                  type="text"
                  className="vn-dropdown__inset-search-input"
                  placeholder="Search..."
                  value={insetQuery}
                  onChange={(e) => setInsetQueryAndNotify(e.target.value)}
                  onKeyDown={handleInsetKeyDown}
                  autoComplete="off"
                  aria-label="Filter options"
                />
              </div>
            </div>
          )}
          <ListComponent {...listProps} />
        </div>
      </Popper>
    </div>
  );

  if (showFieldHelp) {
    return (
      <VNFieldHelpLayout label={label} className="vn-field-help--dropdown" iconAttach="trigger" {...fieldHelpProps}>
        {dropdownContent}
      </VNFieldHelpLayout>
    );
  }

  return dropdownContent;
};

export default VNDropdownBase;
